import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import ChatWidget from "@/components/ChatWidget";
import Seo from "@/components/Seo";
import { UserCircle, MapPin, Sparkle, ArrowRight, Heart } from "@phosphor-icons/react";

export default function Team() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/team")
      .then((r) => setMembers(r.data || []))
      .catch(() => setMembers([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="public-team-page">
      <Seo
        title="Meet the Team — Puppl"
        description="The team behind Puppl — Sunshine Coast dog trainers, assistants and support staff."
      />
      <PublicHeader />

      <section className="bg-[#1b4a76] text-white py-16 lg:py-20 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs uppercase tracking-wider font-semibold text-[#E2B054] mb-3">Our people</p>
          <h1 className="font-heading text-4xl lg:text-6xl font-bold leading-tight mb-4 max-w-3xl">
            Meet the team behind the wags.
          </h1>
          <p className="text-lg lg:text-xl opacity-90 max-w-2xl">
            Real Sunshine Coast dog trainers — fear-free, evidence-based, and a little bit obsessed with your dog.
          </p>
        </div>
      </section>

      <section className="py-16 px-6 lg:px-12 max-w-7xl mx-auto">
        {loading && <div className="text-center text-stone-500 py-16">Loading the team…</div>}
        {!loading && members.length === 0 && (
          <div className="card-pp p-12 text-center">
            <UserCircle size={56} weight="duotone" className="mx-auto mb-3 text-stone-300" />
            <h2 className="font-heading text-2xl font-bold text-stone-900 mb-2">Our team page is being built</h2>
            <p className="text-stone-600 mb-5 max-w-md mx-auto">
              We're putting together photos and bios for the team. In the meantime, drop us a line and we'll match you with the right trainer.
            </p>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
              Get in touch <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        )}

        {!loading && members.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {members.map((m) => (
              <article key={m.id} className="card-pp overflow-hidden flex flex-col hover:-translate-y-1 transition-transform" data-testid={`team-member-${m.id}`}>
                <div className="aspect-[4/5] relative overflow-hidden flex items-center justify-center" style={{ background: `${m.calendar_colour}15` }}>
                  {m.profile_photo ? (
                    <img loading="lazy" src={m.profile_photo} alt={m.name} className="w-full h-full object-contain" />
                  ) : (
                    <span className="font-heading font-bold text-7xl" style={{ color: m.calendar_colour }}>
                      {(m.name || "?").slice(0, 1).toUpperCase()}
                    </span>
                  )}
                  {m.public_credentials && (
                    <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm rounded-full px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold text-[#1b4a76] inline-flex items-center gap-1">
                      <Sparkle size={10} weight="fill" /> {m.public_credentials}
                    </div>
                  )}
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-heading text-xl font-bold text-stone-900">{m.name}</h3>
                  <div className="text-sm text-[#f38838] font-semibold mt-0.5">{m.job_title || "Trainer"}</div>
                  {m.default_region && (
                    <div className="text-xs text-stone-500 inline-flex items-center gap-1 mt-2">
                      <MapPin size={12} weight="duotone" /> {m.default_region}
                    </div>
                  )}
                  {m.bio && <p className="text-sm text-stone-700 mt-3 leading-relaxed line-clamp-6">{m.bio}</p>}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="py-16 px-6 lg:px-12 bg-stone-100">
        <div className="max-w-3xl mx-auto text-center">
          <Heart size={36} weight="duotone" className="text-[#f38838] mx-auto mb-3" />
          <h2 className="font-heading text-3xl font-bold text-stone-900 mb-3">Want to join the team?</h2>
          <p className="text-stone-700 mb-6">
            We&apos;re always keen to hear from passionate dog trainers and assistants. Reach out and tell us about yourself.
          </p>
          <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
            Say hello <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>

      <PublicFooter />
      <ChatWidget />
    </div>
  );
}
